import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { trpc } from '../../lib/trpc';
import { useAsync } from '../../lib/useAsync';
import { Loading, ErrorBox, EmptyBox, StatusPill, PageTitle } from '../../components/admin/ui';
import {
  formatInt,
  formatBRLCompact,
  PLANO_LABEL,
  ASSINATURA_LABEL,
  SAUDE_LABEL,
  SAUDE_COLOR,
} from '../../lib/format';

const SELECT_CLS =
  'h-9 rounded-sm border border-line-2 bg-surface px-2 text-sm text-ink outline-none focus:border-accent';

export function AdminClientes() {
  const [, navigate] = useLocation();
  const { data, loading, error, reload } = useAsync(() => trpc.admin.overview.query());
  const [plano, setPlano] = useState('todos');
  const [status, setStatus] = useState('todos');
  const [busca, setBusca] = useState('');

  const filtrados = useMemo(() => {
    if (!data) return [];
    return data.clientes.filter((c) => {
      if (plano !== 'todos' && c.plano !== plano) return false;
      if (status !== 'todos' && c.statusAssinatura !== status) return false;
      if (busca && !c.nome.toLowerCase().includes(busca.toLowerCase())) return false;
      return true;
    });
  }, [data, plano, status, busca]);

  return (
    <div className="mx-auto max-w-5xl">
      <PageTitle titulo="Clientes" />

      {loading && <Loading />}
      {error && <ErrorBox msg={error} onRetry={reload} />}

      {data && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome…"
              className="h-9 w-56 rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-accent"
            />
            <select value={plano} onChange={(e) => setPlano(e.target.value)} className={SELECT_CLS}>
              <option value="todos">Todos os planos</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="scale">Scale</option>
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={SELECT_CLS}>
              <option value="todos">Todos os status</option>
              <option value="ativa">Ativa</option>
              <option value="inadimplente">Em atraso</option>
              <option value="churned">Cancelada</option>
            </select>
            <span className="ml-auto text-xs text-ink-3">{filtrados.length} cliente(s)</span>
          </div>

          {filtrados.length === 0 ? (
            <EmptyBox titulo="Nenhum cliente" descricao="Ajuste os filtros ou rode o seed de demonstração." />
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 text-left text-[11px] uppercase tracking-wider text-ink-4">
                  <tr>
                    <th className="px-5 py-3 font-medium">Incorporadora</th>
                    <th className="px-3 py-3 font-medium">Plano</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                    <th className="px-3 py-3 font-medium">Saúde</th>
                    <th className="px-3 py-3 font-medium">Contratos</th>
                    <th className="px-3 py-3 text-right font-medium">MRR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {filtrados.map((c) => (
                    <tr
                      key={c.orgId}
                      className="cursor-pointer hover:bg-bg-warm"
                      onClick={() => navigate(`/admin/clientes/${encodeURIComponent(c.orgId)}`)}
                    >
                      <td className="px-5 py-3 font-medium text-ink">{c.nome}</td>
                      <td className="px-3 py-3">
                        <span className="rounded-full border border-line-2 px-2.5 py-0.5 text-xs text-ink-2">
                          {PLANO_LABEL[c.plano]}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-ink-3">{ASSINATURA_LABEL[c.statusAssinatura]}</td>
                      <td className="px-3 py-3">
                        <StatusPill label={SAUDE_LABEL[c.saude]!} color={SAUDE_COLOR[c.saude]!} />
                      </td>
                      <td className="num px-3 py-3 text-ink-2">{formatInt(c.contratos)}</td>
                      <td className="num px-3 py-3 text-right text-ink">
                        {c.mrr > 0 ? formatBRLCompact(c.mrr) : <span className="text-ink-4">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
